import { useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
    type ClientRecord,
    type ClientContactLogEntry,
} from '@/api/clients';
import { useAuthStore } from '@/stores/auth';
import { fmtDob } from '../clientHelpers';
import { ContactLogDialog } from '../ContactLogDialog';

/*
 * Contact log tab — read view + launchers for the add/edit dialog.
 * Rows are reverse-chronological (most recent first) by the entry's
 * stored `date` field; ties fall back to insertion order. The server
 * stores the entries as a JSONB array on the clients row keyed by a
 * stable UUID, so edits/deletes reference the entry id — no index
 * races if two users edit the log at once.
 */
export function ClientContactsTab({ client: c }: { client: ClientRecord }) {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_clients'));
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState<ClientContactLogEntry | null>(null);

    const log = [...(c.contactLog ?? [])].sort((a, b) => {
        if (a.date === b.date) return 0;
        return a.date < b.date ? 1 : -1;
    });

    function openAdd() {
        setEditingEntry(null);
        setDialogOpen(true);
    }

    function openEdit(entry: ClientContactLogEntry) {
        setEditingEntry(entry);
        setDialogOpen(true);
    }

    const header = (
        <CardHeader className="px-5 pt-5 flex items-start justify-between">
            <div>
                <CardTitle>Contact Log</CardTitle>
            </div>
            {canEdit && (
                <Button type="button" size="sm" variant="primary" onClick={openAdd}>
                    <Plus className="w-3.5 h-3.5" /> Add Entry
                </Button>
            )}
        </CardHeader>
    );

    return (
        <>
            <Card className="p-0 overflow-hidden">
                {header}
                {log.length === 0 ? (
                    <p className="px-5 pb-5 text-sm text-text-subtle">No contact log entries yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-5 py-2 font-bold whitespace-nowrap">Date</th>
                                    <th className="px-3 py-2 font-bold">By</th>
                                    <th className="px-3 py-2 font-bold">Type</th>
                                    <th className="px-5 py-2 font-bold">Note</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-12"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {log.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-b border-border last:border-b-0 align-top"
                                    >
                                        <td className="px-5 py-2 text-text-muted whitespace-nowrap">
                                            {row.date ? fmtDob(row.date) : '—'}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                            {row.by || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted">{row.type || '—'}</td>
                                        <td className="px-5 py-2 whitespace-pre-wrap">{row.note || '—'}</td>
                                        {canEdit && (
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    aria-label="Edit entry"
                                                    title="Edit entry"
                                                    onClick={() => openEdit(row)}
                                                    className="p-1 rounded-sm text-text-muted hover:text-text hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {canEdit && (
                <ContactLogDialog
                    clientId={c.id}
                    entry={editingEntry}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                />
            )}
        </>
    );
}
